import React, { useEffect, useState } from 'react'
import AdministrationLayout from '../../../../components/layouts/AdministrationLayout'
import { authHeader, baseUrl } from '../../../../utils';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { ERROR } from '../../../../store/types';
import Chart from "react-apexcharts";
import Loader from '../../../../components/elements/Loader';
import EmptyState from '../../../../components/elements/EmptyState';

const ApplicationAnalytics = () => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null)

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const headers = authHeader()
        const response = await axios.get(`${baseUrl}/dashboards/applications`, { headers })            
        setAnalyticsData(response.data.data)
        setLoading(false)
      } catch (error) {
        console.log(error)
        dispatch({
          type: ERROR,
          error
        })
        setLoading(false)
      }
    }
    fetchAnalytics()
  }, [dispatch]);

  // Stat Card Component
  const StatCard = ({ title, value, subtitle, colorClass = "text-vcm-purple" }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition duration-200">
      <p className="text-sm text-gray-500 mb-2">{title}</p>
      <h3 className={`text-3xl font-bold ${colorClass} mb-1`}>{value}</h3>
      {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
    </div>
  );

  // List Card Component for Top 5
  const ListCard = ({ title, data, emptyText }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
      {data && data.length > 0 ? (
        <div className="space-y-3">
          {data.slice(0, 5).map((item, index) => (
            <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-vcm-light-purple text-vcm-purple text-sm font-semibold">
                  {index + 1}
                </span>
                <span className="text-sm text-gray-700 capitalize">{item._id || 'Not specified'}</span>
              </div>
              <span className="text-sm font-semibold text-vcm-purple">{item.count}</span>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState emptyStateText={emptyText} />
      )}
    </div>
  );

  // Donut Chart Component
  const DonutChart = ({ title, data, emptyText }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
          <EmptyState emptyStateText={emptyText} />
        </div>
      );
    }

    const chartConfig = {
      series: data.map(item => item.count),
      options: {
        chart: {
          type: 'donut',
          fontFamily: 'Outfit, sans-serif',
        },
        labels: data.map(item => item._id || 'Unknown'),
        colors: ['#7C3AED', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE', '#F5F3FF'],
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
          }
        },
        legend: {
          position: 'bottom',
          fontSize: '12px',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#7C3AED'
                }
              }
            }
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 300
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      }
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
        <Chart options={chartConfig.options} series={chartConfig.series} type="donut" height={300} />
      </div>
    );
  };

  // Bar Chart Component
  const BarChartComponent = ({ title, data, emptyText }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
          <EmptyState emptyStateText={emptyText} />
        </div>
      );
    }

    const chartConfig = {
      series: [{
        name: 'Count',
        data: data.map(item => item.count)
      }],
      options: {
        chart: {
          type: 'bar',
          fontFamily: 'Outfit, sans-serif',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            horizontal: false,
            columnWidth: '60%',
            colors: {
              backgroundBarColors: ['#F3F4F6'],
              backgroundBarRadius: 6,
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        colors: ['#7C3AED'],
        xaxis: {
          categories: data.map(item => item._id || 'Unknown'),
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px'
            }
          }
        },
        grid: {
          borderColor: '#F3F4F6',
          strokeDashArray: 4
        },
        tooltip: {
          y: {
            formatter: function(val) {
              return val + " applications"
            }
          }
        }
      }
    };

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">{title}</h3>
        <Chart options={chartConfig.options} series={chartConfig.series} type="bar" height={300} />
      </div>
    );
  };

  if (loading) {
    return (
      <AdministrationLayout pageTitle={`Application Analytics`}>
        <div className="flex items-center justify-center py-20">
          <Loader />
        </div>
      </AdministrationLayout>
    );
  }

  if (!analyticsData) {
    return (
      <AdministrationLayout pageTitle={`Application Analytics`}>
        <div className="flex items-center justify-center py-20">
          <EmptyState emptyStateText="No analytics data available" />
        </div>
      </AdministrationLayout>
    );
  }

  return (
    <AdministrationLayout pageTitle={`Application Analytics`}>
      <div className="space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Total Applications" 
            value={analyticsData.applicationsByClass?.reduce((acc, item) => acc + item.count, 0) || 0}
            colorClass="text-vcm-purple"
          />
          <StatCard 
            title="Completed Applications" 
            value={`${analyticsData.percentageOfCompletedApplications?.toFixed(1) || 0}%`}
            subtitle="Completion rate"
            colorClass="text-vcm-green"
          />
          <StatCard 
            title="Admitted" 
            value={analyticsData.applicationsByStatus?.find(s => s._id === 'ADMITTED')?.count || 0}
            subtitle="Successfully admitted"
            colorClass="text-green-600"
          />
          <StatCard 
            title="Avg. Documents" 
            value={analyticsData.averageNumberOfDocuments?.[0]?.averageDocuments?.toFixed(1) || 0}
            subtitle="Per application"
            colorClass="text-blue-600"
          />
        </div>

        {/* Status and Gender Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DonutChart 
            title="Applications by Status" 
            data={analyticsData.applicationsByStatus}
            emptyText="No status data available"
          />
          <DonutChart 
            title="Applications by Gender" 
            data={analyticsData.applicationsByGender?.filter(item => item._id !== null)}
            emptyText="No gender data available"
          />
        </div>

        {/* Class and Age Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <BarChartComponent 
            title="Applications by Class" 
            data={analyticsData.applicationsByClass?.filter(item => item._id !== null)}
            emptyText="No class data available"
          />
          <BarChartComponent 
            title="Age Distribution" 
            data={analyticsData.ageDistribution?.sort((a, b) => b.count - a.count).slice(0, 10)}
            emptyText="No age data available"
          />
        </div>

        {/* Top Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ListCard 
            title="Top 5 Places of Birth" 
            data={analyticsData.applicationsByPlaceOfBirth?.filter(item => item._id !== null).sort((a, b) => b.count - a.count)}
            emptyText="No place of birth data"
          />
          <ListCard 
            title="Top 5 Sports Interests" 
            data={analyticsData.popularSportsInterests?.sort((a, b) => b.count - a.count)}
            emptyText="No sports data"
          />
          <ListCard 
            title="Top 5 Hobbies" 
            data={analyticsData.popularHobbies?.sort((a, b) => b.count - a.count)}
            emptyText="No hobbies data"
          />
        </div>

        {/* Examination Centers Performance */}
        {analyticsData.examinationsByCenter && analyticsData.examinationsByCenter.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Examination Centers Overview</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Center
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Examinations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Average Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Performance
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData.examinationsByCenter
                    .filter(center => center._id !== null)
                    .map((center) => {
                      const avgScore = analyticsData.averageExamScoresByCenter?.find(
                        score => score._id === center._id
                      );
                      const score = avgScore?.averageScore || 0;
                      const performanceColor = score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600';
                      const performanceBg = score >= 80 ? 'bg-green-100' : score >= 60 ? 'bg-yellow-100' : 'bg-red-100';

                      return (
                        <tr key={center._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {center.centerName.substring(0, 45)}...
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {center.count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {score.toFixed(2)}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${performanceBg} ${performanceColor}`}>
                              {score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : 'Needs Improvement'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdministrationLayout>
  )
}

export default ApplicationAnalytics